'use client'

import React from 'react'
import { cn } from '@/utilities/ui'
import { TYPE_SPECIMENS, type TypeSpecimen } from './constants'
import { useInView } from './Reveal'

const SpecimenCard: React.FC<{ specimen: TypeSpecimen; index: number }> = ({
  specimen,
  index,
}) => {
  const { ref, inView } = useInView<HTMLDivElement>(0.25)

  return (
    <div
      ref={ref}
      style={{ transitionDelay: inView ? `${index * 120}ms` : '0ms' }}
      className={cn(
        'group transition-[opacity,transform] duration-700 ease-out',
        'motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:scale-100',
        inView ? 'scale-100 opacity-100' : 'scale-[0.97] opacity-0',
      )}
    >
      {/* Card flips to black-on-white on hover; reveal lives on the parent so the
          colour transition stays snappy instead of inheriting the 700ms reveal. */}
      <div
        className={cn(
          'flex h-full flex-col gap-6 rounded-[16px] border border-civo-cloud p-8 sm:p-10',
          'transition-colors duration-300 ease-out group-hover:border-civo-black group-hover:bg-civo-black',
          'motion-reduce:transition-none',
        )}
      >
        {/* Big glyph eases up in scale on hover, anchored at the baseline.
            Decorative specimen - the family name below carries the real info. */}
        <div aria-hidden="true" className="inline-block self-start leading-none">
          <span
            className={cn(
              specimen.fontClass,
              'block origin-bottom-left text-8xl font-light text-civo-black transition-[transform,color] duration-300 ease-out group-hover:scale-[1.04] group-hover:text-civo-lime motion-reduce:transition-colors motion-reduce:group-hover:scale-100 lg:text-9xl',
            )}
          >
            Ag
          </span>
        </div>

        <div className="flex flex-col gap-3">
          <h3 className="subheading-small text-civo-black transition-colors duration-300 group-hover:text-civo-white">
            {specimen.family}
          </h3>
          <p className="body-small text-black/60 transition-colors duration-300 group-hover:text-white/60">
            {specimen.role}
          </p>

          <div className="mt-1 flex flex-wrap gap-2">
            {specimen.weights.map((w) => (
              <span
                key={w}
                className="body-xsmall rounded-full border border-civo-cloud px-3.5 py-1.5 text-black/70 transition-colors duration-300 group-hover:border-white/30 group-hover:text-white/70 motion-reduce:transition-none"
              >
                {w}
              </span>
            ))}
          </div>

          <p className="body-small mt-3 text-black/60 transition-colors duration-300 group-hover:text-white/60">
            {specimen.tracking}
          </p>

          {/* Sample line types itself out on scroll */}
          <p
            className={cn(
              specimen.fontClass,
              'brand-typeline body-large mt-1 text-civo-black transition-colors duration-300 group-hover:text-civo-white',
              inView && 'is-typing',
            )}
          >
            {specimen.sample}
            <span aria-hidden="true" className="brand-caret ml-0.5 inline-block">
              |
            </span>
          </p>
        </div>
      </div>
    </div>
  )
}

export const TypographySpecimen: React.FC = () => (
  <div className="grid gap-6 md:grid-cols-2">
    {TYPE_SPECIMENS.map((specimen, i) => (
      <SpecimenCard key={specimen.family} specimen={specimen} index={i} />
    ))}
  </div>
)
